import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { finalize, take } from 'rxjs/operators';

import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { PrintService } from '../../../services/print.service';

export type Tpl3plThroughputMetric = 'orders' | 'exported' | 'invoiced';

export interface Tpl3plOiSeriesRow {
	date: string;
	orders?: number;
	exported?: number;
	invoiced?: number;
}

@Component({
	selector: 'app-tpl3pl-throughput-series',
	templateUrl: './tpl3pl-throughput-series.component.html',
	styleUrls: ['./tpl3pl-throughput-series.component.scss']
})
export class Tpl3plThroughputSeriesComponent {
	@Input() metric: Tpl3plThroughputMetric = 'orders';
	@Input() series: Tpl3plOiSeriesRow[] = [];
	/** Detail card title, e.g. "Orders by day" */
	@Input() reportTitle = '';
	/** Human-readable selected range, e.g. "2026-04-01 → 2026-04-27 · 27 day(s)" */
	@Input() dateRangeLabel = '';

	@ViewChild('printTable') printTable: ElementRef<HTMLElement>;

	pdfExporting = false;

	constructor(
		private globalSearchService: GlobalSearchService,
		private printService: PrintService
	) {}

	valueFor(row: Tpl3plOiSeriesRow): number {
		if (this.metric === 'orders') {
			return row.orders ?? 0;
		}
		if (this.metric === 'exported') {
			return row.exported ?? 0;
		}
		return row.invoiced ?? 0;
	}

	columnLabel(): string {
		if (this.metric === 'orders') {
			return 'Orders';
		}
		if (this.metric === 'exported') {
			return 'Exported';
		}
		return 'Invoiced';
	}

	get seriesDayCount(): number {
		return Array.isArray(this.series) ? this.series.length : 0;
	}

	get seriesTotal(): number {
		if (!this.series?.length) {
			return 0;
		}
		return this.series.reduce((s, r) => s + this.valueFor(r), 0);
	}

	get seriesAvgPerDay(): number {
		const n = this.seriesDayCount;
		return n > 0 ? this.seriesTotal / n : 0;
	}

	exportXlsx(): void {
		if (!this.series?.length) {
			return;
		}
		const col = this.columnLabel();
		const body = this.series.map((r) => ({ Date: r.date, [col]: this.valueFor(r) }));
		const title = (this.reportTitle || `${col} by day`).trim();
		const parts = [
			this.dateRangeLabel?.trim() || '',
			`Total: ${this.seriesTotal} · Days: ${this.seriesDayCount} · Avg/day: ${this.seriesAvgPerDay.toFixed(2)}`,
		].filter(Boolean);
		const subtitle = parts.join(' · ');
		const slug = col.replace(/\s+/g, '');
		this.globalSearchService.exportJsonToExcelWithTitle(
			title,
			subtitle,
			body,
			`Tpl3plThroughput_${slug}_${new Date().toISOString().slice(0, 10)}`
		);
	}

	exportPdf(): void {
		if (!this.printTable?.nativeElement || this.pdfExporting || !this.series?.length) {
			return;
		}
		this.pdfExporting = true;
		const encoded = this.globalSearchService.base64encode(this.printTable.nativeElement.innerHTML);
		const col = this.columnLabel();
		const filename = `Tpl3plThroughput_${col.replace(/\s+/g, '')}_${new Date().toISOString().slice(0, 10)}`;
		const title = (this.reportTitle || `${col} by day`).trim();
		const subtitle = [this.dateRangeLabel?.trim() || '', 'Exported ' + new Date().toLocaleString()]
			.filter(Boolean)
			.join(' · ');
		this.printService
			.pdf({
				content: encoded,
				filename,
				title,
				subtitle,
			})
			.pipe(
				take(1),
				finalize(() => {
					this.pdfExporting = false;
				})
			)
			.subscribe({
				next: (result: any) => {
					this.globalSearchService.downloadPdf(result.content, filename);
				},
			});
	}
}
